import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  Boxes,
  Calendar,
  ChevronDown,
  CreditCard,
  HelpCircle,
  Send,
  ShoppingBag,
  TrendingUp,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { PeriodPreset, PeriodSpec, QuickQueryCategory, QuickQueryDef } from '@contracts';
import { resolvePeriod } from '@/lib/period';
import { DASHBOARD_MODE } from '@/lib/config';
import { PRESETS } from '@/services/presetQueries';

type QQ = { id: string; text: string; needsPeriod?: boolean; defaultPeriod?: PeriodPreset };
type Group = { category: string; icon: LucideIcon; queries: QQ[] };

/** Orden e íconos de categoría (la fuente de las consultas es Odoo vía contrato). */
const CATEGORY_ORDER: QuickQueryCategory[] = ['Ventas', 'Clientes', 'Inventario', 'Pedidos', 'Finanzas'];
const CATEGORY_ICONS: Record<QuickQueryCategory, LucideIcon> = {
  Ventas: TrendingUp,
  Clientes: Users,
  Inventario: Boxes,
  Pedidos: ShoppingBag,
  Finanzas: CreditCard,
};

/** Arma los grupos desde el contrato; ignora presets sin resolver en el front (no truena). */
function buildGroups(defs: QuickQueryDef[]): Group[] {
  const byCat = new Map<string, QQ[]>();
  for (const d of defs) {
    if (!PRESETS[d.presetId]) continue;
    const list = byCat.get(d.category) ?? [];
    list.push({ id: d.presetId, text: d.label, needsPeriod: d.needsPeriod, defaultPeriod: d.defaultPeriod ?? undefined });
    byCat.set(d.category, list);
  }
  return CATEGORY_ORDER.filter((c) => byCat.has(c)).map((c) => ({
    category: c,
    icon: CATEGORY_ICONS[c],
    queries: byCat.get(c)!,
  }));
}

/** Fallback hardcodeado (resiliencia si Odoo/BridgeHub no entregan config). */
const DEFAULT_GROUPS: Group[] = [
  {
    category: 'Ventas',
    icon: TrendingUp,
    queries: [
      { id: 'sales', text: '¿Cuánto vendí hoy?', needsPeriod: true, defaultPeriod: 'today' },
      { id: 'sales', text: 'Ventas de esta semana', needsPeriod: true, defaultPeriod: 'this_week' },
      { id: 'sales', text: 'Ventas del mes actual', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'compareMonths', text: 'Comparar este mes vs el anterior' },
      { id: 'bestDay', text: '¿Cuál fue mi mejor día de ventas?', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'avgTicket', text: 'Ticket promedio de compra', needsPeriod: true, defaultPeriod: 'this_month' },
    ],
  },
  {
    category: 'Clientes',
    icon: Users,
    queries: [
      { id: 'newClients', text: '¿Cuántos clientes registré?', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'bestSeller', text: '¿Quién es el mejor vendedor?', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'branches', text: 'Ranking de sucursales', needsPeriod: true, defaultPeriod: 'this_month' },
    ],
  },
  {
    category: 'Inventario',
    icon: Boxes,
    queries: [
      { id: 'productsTop', text: 'Productos más vendidos', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'productsBottom', text: 'Productos menos vendidos', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'units', text: '¿Cuántas unidades vendí?', needsPeriod: true, defaultPeriod: 'this_month' },
    ],
  },
  {
    category: 'Pedidos',
    icon: ShoppingBag,
    queries: [
      { id: 'apartadosActivos', text: '¿Cuántos apartados activos hay?' },
      { id: 'porCobrar', text: 'Monto por cobrar de apartados' },
      { id: 'albaranes', text: 'Albaranes pendientes' },
    ],
  },
  {
    category: 'Finanzas',
    icon: CreditCard,
    queries: [
      { id: 'paymentMix', text: 'Mix de métodos de pago', needsPeriod: true, defaultPeriod: 'this_month' },
      { id: 'storeCredit', text: 'Store credit disponible' },
      { id: 'trend', text: 'Tendencia de ventas', needsPeriod: true, defaultPeriod: 'this_month' },
    ],
  },
];

/** Consultas rápidas del dashboard de LOGÍSTICA (modo logística). Solo inventario,
 * resurtido, transferencias y compras — nada de ventas. */
const LOGISTICS_GROUPS: Group[] = [
  {
    category: 'Resurtido',
    icon: Boxes,
    queries: [
      { id: 'urgencyKpis', text: '¿Cuántos productos pedir ya?' },
      { id: 'resupplyUrgent', text: 'Productos por quebrarse (urgentes)' },
      { id: 'resupplyForecast', text: 'Calendario de resurtido' },
      { id: 'resupplyCount', text: '¿Cuántos productos a resurtir?' },
    ],
  },
  {
    category: 'Inventario',
    icon: Boxes,
    queries: [
      { id: 'inventoryValue', text: '¿Cuánto vale mi inventario?' },
      { id: 'inventoryByLocation', text: 'Inventario por ubicación' },
    ],
  },
  {
    category: 'Transferencias',
    icon: ShoppingBag,
    queries: [
      { id: 'bringFromStores', text: '¿Qué traer de otras tiendas?' },
      { id: 'stockTransfers', text: 'Transferencias entre tiendas' },
    ],
  },
  {
    category: 'Compras',
    icon: CreditCard,
    queries: [
      { id: 'purchaseOrders', text: 'Órdenes de compra' },
    ],
  },
];

const PERIOD_OPTS: { value: PeriodPreset | 'custom' | 'none'; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'this_week', label: 'Esta semana' },
  { value: 'this_month', label: 'Este mes' },
  { value: 'last_month', label: 'Mes pasado' },
  { value: 'last_30d', label: 'Últ. 30 días' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'ytd', label: 'Este año' },
  { value: 'custom', label: 'Personalizado' },
  { value: 'none', label: 'Sin período' },
];

export interface PresetPick {
  id: string;
  label: string; // texto a mostrar en el chat (puede incluir el periodo)
  period: PeriodSpec | null; // null = sin periodo / usar default
}

export function QuickQueries({
  onPreset,
  open,
  onToggle,
  queries,
}: {
  onPreset: (p: PresetPick) => void;
  open: boolean;
  onToggle: () => void;
  /** Consultas del contrato (Odoo). Si falta/vacío → fallback hardcodeado. */
  queries?: QuickQueryDef[];
}) {
  const groups = useMemo<Group[]>(() => {
    const built = queries && queries.length ? buildGroups(queries) : [];
    if (built.length) return built;
    // Sin config de Odoo → fallback por modo: logística usa sus consultas de
    // inventario; comercio usa las de ventas.
    return DASHBOARD_MODE === 'logistica' ? LOGISTICS_GROUPS : DEFAULT_GROUPS;
  }, [queries]);

  const [cat, setCat] = useState(0);
  const [pending, setPending] = useState<QQ | null>(null);
  const [periodSel, setPeriodSel] = useState<PeriodPreset | 'custom' | 'none'>('this_month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const activeGroup = groups[cat] ?? groups[0];

  const pickQuery = (q: QQ) => {
    if (!q.needsPeriod) {
      onPreset({ id: q.id, label: q.text, period: null });
      return;
    }
    setPending(q);
    setPeriodSel(q.defaultPeriod ?? 'this_month');
  };

  const confirmPeriod = () => {
    if (!pending) return;
    if (periodSel === 'none') {
      onPreset({ id: pending.id, label: pending.text, period: null });
    } else {
      const spec: PeriodSpec = periodSel === 'custom' ? { from, to } : periodSel;
      const { from: f, to: t } = resolvePeriod(spec);
      onPreset({ id: pending.id, label: `${pending.text} — Período: del ${f} al ${t}`, period: spec });
    }
    setPending(null);
  };

  return (
    <div className="border-t border-slate-100 bg-white">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 hover:text-slate-700"
      >
        <Zap className="h-3.5 w-3.5 text-orange-500" />
        Consultas rápidas
        <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>

      {open &&
        (pending ? (
          /* ── Paso: elegir periodo ────────────────────────────────────── */
          <div className="px-3 pb-3">
            <div className="mb-2.5 flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-medium text-slate-700">
              <HelpCircle className="h-4 w-4 shrink-0 text-orange-500" />
              {pending.text}
            </div>
            <p className="mb-1.5 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
              <Calendar className="h-3.5 w-3.5 text-orange-500" /> ¿Para qué período?
            </p>
            <div className="mb-2.5 flex flex-wrap gap-1.5">
              {PERIOD_OPTS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPeriodSel(p.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    periodSel === p.value
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {periodSel === 'custom' && (
              <div className="mb-2.5 flex items-center gap-2">
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-orange-400"
                />
                <span className="text-xs text-slate-400">a</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-orange-400"
                />
              </div>
            )}
            {/* Botones: Volver (ghost) + Enviar (CTA prominente) */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPending(null)}
                className="flex items-center gap-1 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
              >
                <ArrowLeft className="h-4 w-4" /> Volver
              </button>
              <button
                onClick={confirmPeriod}
                disabled={periodSel === 'custom' && (!from || !to)}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-orange-500/30 transition hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/40 disabled:opacity-40 disabled:shadow-none"
              >
                <Send className="h-4 w-4" /> Enviar consulta
              </button>
            </div>
          </div>
        ) : (
          /* ── Categorías + preguntas ──────────────────────────────────── */
          <div className="px-3 pb-3">
            <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1">
              {groups.map((g, i) => {
                const Icon = g.icon;
                const active = i === cat;
                return (
                  <button
                    key={g.category}
                    onClick={() => setCat(i)}
                    className={`flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                      active ? 'bg-orange-500 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {g.category}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {activeGroup.queries.map((q) => (
                <button
                  key={q.text}
                  onClick={() => pickQuery(q)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-600 transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700"
                >
                  {q.text}
                </button>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
